import { Root } from "protobufjs";
import { inject } from "vue";
import pb from "@pb/index.json";
import EventEmitter from "eventemitter3";
import { notification } from "ant-design-vue";

import { encode, decode } from "../pbUtils";
import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  concatArrayBuffers,
  unpackLonglinkPacket,
  packLonglinkPacket,
} from "./unitPacket";
import COMMAND from "./command";
import { heartbeatPro, loginPro, PlayerStatus } from "./protocol";
import * as Accounts from "@js/account";
import { IS_PC, clientHeader, websocketUrl } from "@/config";
import eventsCenter, { EVENTCMD } from "@js/events";
import { WEBSOCKET } from "@js/events";
import Error from "@js/error";
import Logger from "@js/logger";
import { INamespace } from "protobufjs";
import { usePlayGameStore } from "@/store/playGame";

const root = Root.fromJSON(pb as INamespace);

console.log("root:", root);
const PBJSONROOT = pb.nested.yunpb.nested;
const PBOPERATIONTYPE = PBJSONROOT.OperationType.values;

const globalRequestMap: Record<
  number,
  { resolve: any; reject: any; opts: any }
> = {};

console.log("COMMAND", COMMAND);

const OpenNotification = (
  type: any,
  { message = "菜机提示", description = "" }
) => {
  notification[type]({ message, description });
};

class DYLongLinkClient {
  // static instance = new DYLongLinkClient();

  static getInstance(): DYLongLinkClient {
    if (!this.instance) {
      this.instance = new DYLongLinkClient();
    }
    return this.instance;
  }

  static CLOUNDGAMECMD = PBJSONROOT.CloudGameCmd.values;
  static PBOPERATIONTYPE = PBOPERATIONTYPE;

  static uniqueSeqId = 1024;

  private static instance: DYLongLinkClient;
  private eventEmitter: EventEmitter;
  private isConencted: boolean;
  private isConencting: boolean;
  private isReConenct: boolean;
  private accountInfo: any;
  private heartbeatTimer: any;

  private ws: any;

  public isConnectionReady() {
    return this.isConencted;
  }

  constructor() {
    console.log("DYLongLinkClient constructor");

    this.eventEmitter = new EventEmitter();
    this.isConencted = false;
    this.isConencting = false;
    this.isReConenct = false;
    this.heartbeatTimer = null;
    // pc端跳到web端小卖部后，不建立长连接，否则pc端会退出登录
    if (IS_PC) return;
    // 用户登录就连接websocket
    if (Accounts.isLogin()) {
      this.setup();
    }

    // 用户登录改变也要重连websocket
    eventsCenter.on(
      Accounts.ACCOUNT_EVENTS.LOGIN_SUCCESS,
      this.onLoginKeyChanged.bind(this)
    );
    this.startIntevalCheckPlayerStatus();
  }

  // 用户登录改变重连websocket
  onLoginKeyChanged() {
    if (!Accounts.isLogin()) {
      return;
    }

    if (!this.isConnectionReady()) {
      this.setup();
    }
  }

  // 连接websocket
  setup() {
    console.log("setup websocket");
    this.connect();
  }

  // 通过websocket发送请求
  public sendRequest(settings: {
    reqDesction: string;
    resDesction: string;
    op?: number;
    serverName?: string;
    funcName?: string;
    data?: any;
    showNotification?: boolean;
  }) {
    if (!this.ws || !this.isConencted) {
      return Promise.reject({
        ret: Error.CONST.websocketNotConnected,
      });
    }

    const useBase64 = false;

    // 包装请求的数据包
    // 作 ts 兼容处理
    const { seqId, opts, packStr, packBuffer } = this.bulidProtocolMsgPacket(
      Object.assign({ op: 0 }, settings),
      useBase64
    );

    return new Promise((resolve, reject) => {
      if (useBase64) {
        this.ws.send(packStr);
      } else {
        console.log("packBuffer", packBuffer);
        this.ws.send(packBuffer);
      }

      globalRequestMap[seqId] = {
        resolve: resolve,
        reject: reject,
        opts: opts,
      };
    });
  }

  // 包装protobuf的请求包
  public bulidProtocolMsgPacket(
    settings: {
      reqDesction: string;
      op: number;
      resDesction: string;
      serverName?: string;
      funcName?: string;
      data?: any;
      [key: string]: any;
    },
    useBase64?: boolean
  ) {
    // base64编码会用这个
    let packStr = "";
    // 非base64用这个
    let packBuffer;

    // 请求配置
    const defaults = {
      rpcInDesction: "RPCInput",
      rpcOutDesction: "RPCOutput",
      op: PBOPERATIONTYPE.OP_SEND_SMS,
    };
    const opts = Object.assign(defaults, settings);

    // 增加请求队列id，用于promise的resolve
    const seqId = DYLongLinkClient.uniqueSeqId++;

    if (settings && settings.data) {
      // 编码req数据
      const reqBuffer = encode(root, opts.reqDesction, opts.data);

      // 编码rpc数据
      const rpcBuffer = encode(root, opts.rpcInDesction, {
        obj: opts.serverName,
        func: opts.funcName,
        req: reqBuffer,
        opt: {
          appid: String(PBJSONROOT.AppID.values.CHIKII_APP_ID),
          client: clientHeader,
        },
      }) as ArrayBuffer;

      // base64的处理
      if (useBase64) {
        const rpcStr = arrayBufferToBase64(rpcBuffer);

        packStr = this.packWsBody({
          bufferStr: rpcStr,
          op: opts.op,
          seq: seqId,
        });
      } else {
        // 二进制包的处理，拼接arrayBuffer
        const headBuffer = packLonglinkPacket(
          seqId,
          settings.op,
          rpcBuffer.byteLength
        );
        packBuffer = concatArrayBuffers(headBuffer.buffer, rpcBuffer);
      }
    } else {
      // 发空包的处理
      if (useBase64) {
        packStr = this.packWsBody({
          op: opts.op,
          bufferStr: "",
          seq: seqId,
        });
      } else {
        packBuffer = packLonglinkPacket(seqId, settings.op, 0);
      }
    }
    console.log(`build request : ${seqId}`, opts);
    return {
      packStr,
      packBuffer,
      opts,
      seqId,
    };
  }

  public bulidProtocolMsg(settings: {
    reqDesction: string;
    resDesction: string;
    serverName?: string;
    funcName?: string;
    op?: number;
    data?: any;
  }) {
    const defaults = {
      rpcInDesction: "RPCInput",
      rpcOutDesction: "RPCOutput",
      op: PBOPERATIONTYPE.OP_SEND_SMS,
    };
    const opts = Object.assign(defaults, settings);

    if (settings && settings.data) {
      return encode(root, opts.reqDesction, opts.data);
    }
    return null;
  }

  public bulidProtocolRPCMsg(settings: {
    func?: string;
    obj?: string;
    req?: any;
  }) {
    if (settings && settings.req) {
      return encode(root, "RPCInput", settings);
    }
    return null;
  }

  // 转成json字符串去传送
  private packWsBody(opts: { op: number; bufferStr: string; seq: number }) {
    const obj = {
      ver: 1,
      op: opts.op,
      seq: opts.seq,
      body: opts.bufferStr,
    };
    const result = JSON.stringify(obj);
    return result;
  }

  // 处理websocket接收的消息
  private onReceiveMessage(evt: any) {
    console.log("websocket 消息通信", evt);
    if (!evt.data || evt.data.length <= 0) {
      return;
    }

    if (typeof evt.data === "string") {
      const message: string = evt.data;
      const unpackactArr = JSON.parse(message);
      if (unpackactArr) {
        for (let i = 0; i < unpackactArr.length; i++) {
          const obj = unpackactArr[i];
          this.handleReceiveInfo(obj);
        }
      }
    } else if (evt.data instanceof Blob) {
      const fileReader = new FileReader();
      fileReader.onload = (event: any) => {
        try {
          this.handleRawPacketMessage(event.target.result);
        } catch (e) {
          console.log("parse rawpacket error ", e);
        }
      };
      fileReader.readAsArrayBuffer(evt.data);
    }
  }

  private handleRawPacketMessage(buffer: ArrayBuffer) {
    let seekOffset = 0;
    const bytes = new Uint8Array(buffer);

    while (seekOffset < buffer.byteLength) {
      const headerPack: any = unpackLonglinkPacket(bytes, seekOffset);

      console.log(
        `headerpack cmdid:${headerPack.cmdid} seq:${headerPack.seq}`,
        headerPack.bodyData
      );

      if (headerPack.seq > 0 && headerPack.bodyData) {
        const reqInfo = globalRequestMap[headerPack.seq];
        const opts = reqInfo.opts;
        const rpcOut = decode(root, opts.rpcOutDesction, headerPack.bodyData);
        let result = headerPack;

        console.log(
          `handleRawPacketMessage ${headerPack.seq} ${rpcOut.func}`,
          opts,
          rpcOut
        );
        Logger.log(`handle Rpc invoke result ${headerPack.seq} ${rpcOut.func}`);

        if (rpcOut && rpcOut.ret === 0) {
          if (opts.resDesction) {
            result = rpcOut.rsp
              ? decode(root, opts.resDesction, rpcOut.rsp as any)
              : result;
          }
          Logger.log(
            `handle Rpc invoke result success ${headerPack.seq} ${rpcOut.func}`
          );
          reqInfo.resolve(result);
        } else {
          Logger.log(
            `handle Rpc invoke result success ${headerPack.seq} ${rpcOut.func} error:${rpcOut.ret}`
          );

          reqInfo.reject({
            ret: Error.CONST.rpcinputParseError,
            rpcOut: rpcOut,
            msg: "ret error",
            obj: result,
            opts: reqInfo.opts,
          });

          opts.showNotification &&
            OpenNotification("warning", {
              message: opts.notificationText || Error.errorMessage(rpcOut),
            });
        }

        delete globalRequestMap[headerPack.seq];
      } else if (headerPack.seq == 0 && headerPack.bodyData) {
        try {
          const serverPush = decode(root, "ServerPush", headerPack.bodyData);
          const pbObjectDesc = COMMAND[serverPush.cmdId];
          if (pbObjectDesc) {
            const rsp = decode(root, pbObjectDesc, serverPush.body);

            if (rsp) {
              this.eventEmitter.emit(serverPush.cmdId as string, {
                cmd: serverPush.cmdId,
                obj: rsp,
              });
            }
            console.log(`serverPush ${serverPush.cmdId} rsp:`, rsp);
            Logger.log(`serverPush ${serverPush.cmdId} ${JSON.stringify(rsp)}`);
          } else {
            console.log(`serverPush ${serverPush.cmdId}:`, serverPush);
          }
        } catch (e) {
          console.log("server push error", {
            e: e,
          });
        }
      }

      seekOffset += headerPack.headerLen + headerPack.bodyLength;
    }
  }

  // 处理响应，根据seqId映射对应请求
  private handleReceiveInfo(unpackactObject: any) {
    const reqInfo = globalRequestMap[unpackactObject.seq];
    let result = unpackactObject;
    if (reqInfo) {
      const opts = reqInfo.opts;
      if (
        unpackactObject.op > 0 &&
        unpackactObject.body &&
        unpackactObject.body.length
      ) {
        // 处理客户端主动发送消息时的响应
        try {
          const resBuffer = base64ToArrayBuffer(unpackactObject.body);
          const rpcOut = decode(root, opts.rpcOutDesction, resBuffer);
          if (rpcOut && rpcOut.ret === 0) {
            if (opts.resDesction) {
              result = rpcOut.rsp
                ? decode(root, opts.resDesction, rpcOut.rsp as any)
                : result;
            }
            reqInfo.resolve(result);
          } else {
            reqInfo.reject({
              ret: Error.CONST.rpcinputParseError,
              rpcOut: rpcOut,
              msg: "ret error",
              obj: unpackactObject,
              opts: reqInfo.opts,
            });

            opts.showNotification &&
              OpenNotification("warning", {
                message: opts.notificationText || Error.errorMessage(rpcOut),
              });
          }
        } catch (e) {
          reqInfo.reject({
            ret: Error.CONST.rpcinputException,
            msg: "parse error",
            errInfo: e,
            obj: unpackactObject,
            opts: reqInfo.opts,
          });

          opts.showNotification &&
            OpenNotification("warning", {
              message:
                opts.notificationText ||
                Error.clientErrorMessage(Error.CONST.clientError),
            });
        }
      }
      delete globalRequestMap[unpackactObject.seq];
    } else if (
      unpackactObject.seq == 0 &&
      unpackactObject.body &&
      unpackactObject.body.length
    ) {
      // 处理服务端主动推送的消息
      try {
        const serverPushBuffer = base64ToArrayBuffer(unpackactObject.body);
        const serverPush = decode(root, "ServerPush", serverPushBuffer);
        const pbObjectDesc = COMMAND[serverPush.cmdId];
        if (pbObjectDesc) {
          const rsp = decode(root, pbObjectDesc, serverPush.body as Uint8Array);
          if (rsp) {
            this.eventEmitter.emit(serverPush.cmdId, {
              cmd: serverPush.cmdId,
              obj: rsp,
            });
          }
          console.log("serverPush rsp:", rsp);
        } else {
          console.log("serverPush :", serverPush);
        }
      } catch (e) {
        console.log("server push error", {
          e: e,
          unpackactObject: unpackactObject,
        });
      }
    }

    // console.log("unpackactObject :", unpackactObject)
  }

  // 连接关闭
  private handleSocketClose() {
    Logger.log(`Connection closed ... `);

    clearInterval(this.heartbeatTimer);

    if (this.isReConenct) {
      this.reset();
    } else {
      this.isConencted = false;
      this.isConencting = false;

      if (Accounts.isLogin()) {
        this.reset();
      }
    }
  }

  // websocket连接时
  private async handleSocketOpen() {
    Logger.log("Connection open ...");

    // websocket心跳重连，防止自动断开连接
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartBeat();
    }, 30000);

    this.isConencted = true;
    this.isConencting = false;

    const loginKey = Accounts.getLoginKey();
    const uuid = Accounts.getUUID();
    console.log("uuid", uuid);

    // 获取accountId，创建连接成功
    const authResult: any = await this.sendRequest(loginPro(loginKey, uuid));

    this.accountInfo = authResult;
    this.accountInfo.sAccountId = String(authResult.accountId);

    console.log("authResult : ", authResult, eventsCenter);

    Accounts.setAccountId(authResult.accountId);
    eventsCenter.emit(WEBSOCKET.CONNECTED);

    this.checkPlayerStatus();
  }

  private startIntevalCheckPlayerStatus() {
    // setInterval(() => {
    //   this.checkPlayerStatus();
    // },1000 * 60 * 4.5);
  }

  // 获取玩家的状态
  private async checkPlayerStatus() {
    if (!this.isConencted || !this.accountInfo) {
      return;
    }

    const playGameStore = usePlayGameStore();
    Logger.log("checkPlayerStatus");

    const playerStatus = await this.sendRequest(
      PlayerStatus(this.accountInfo.accountId)
    );
    playGameStore.setPlayerStatus(playerStatus);
    const { status } = playerStatus as pro.yunpb.IGetPlayerStatusRes;
    window.gameStateMgr.onPageReload(status as number);
    // eventsCenter.emit(EVENTCMD.PlayerStatus, {
    //   cmd: EVENTCMD.PlayerStatus,
    //   obj: playerStatus,
    // });
  }

  // 连接出错重连
  private handleSocketError(err: any) {
    Logger.log(`Connection socket error ... ${JSON.stringify(err)}`);
    console.log("websocket 连接错误", err);

    this.isReConenct = true;
    this.reset();
  }

  // 心跳重连
  private sendHeartBeat() {
    console.log("sendHeartBeat ...");
    this.sendRequest(heartbeatPro());
  }

  // 关闭连接重连
  private reset() {
    Logger.log(`Connection reseting ... `);

    this.close();

    setTimeout(() => {
      this.connect();
    }, 2000);
  }

  // 关闭websocket
  private close() {
    Logger.log(`Connection close ... `);

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConencted = false;
    this.isConencting = false;
  }

  // 连接websocket
  connect() {
    if (this.isConencted || this.isConencting) {
      return;
    }

    try {
      this.isConencting = true;
      const ws = new WebSocket(websocketUrl);
      this.ws = ws;

      ws.onopen = this.handleSocketOpen.bind(this);
      ws.onmessage = this.onReceiveMessage.bind(this);
      ws.onclose = this.handleSocketClose.bind(this);
      ws.onerror = this.handleSocketError.bind(this);
    } catch (e) {
      console.log("connect error : ", e);
    }
  }

  disconnect() {
    this.isReConenct = false;
    setTimeout(() => {
      this.close();
    }, 2000);
  }

  on(command: string, callback: any) {
    this.eventEmitter.on(command, callback);
  }

  addEventListener(command: string, callback: any) {
    this.eventEmitter.on(command, callback);
  }
}

DYLongLinkClient.getInstance();

export default DYLongLinkClient;
